import { useCallback, useState } from "react";
import { Archive, Lock, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { money, payApi, type JobRole, type PayRate } from "../../../api/pay";
import { adminApi } from "../../../api/resources";
import { Button, TextInput } from "../../../components/ui";
import { DataTable, LoadState, useLoad } from "../shared";

const sel = "h-11 rounded-sm border border-white/15 bg-surface px-3 text-sm text-white";

function RoleList({ roles, onChanged }: { roles: JobRole[]; onChanged: () => void }) {
  const [name, setName] = useState("");
  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast.success(ok);
      onChanged();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <div className="rounded-sm border border-white/10 p-5" data-testid="roles-panel">
      <p className="label-caps mb-3 text-signal">Job roles</p>
      <ul className="mb-4 space-y-2">
        {roles.map((r) => (
          <li key={r.id} className="flex items-center gap-2 text-sm" data-testid={`role-row-${r.name}`}>
            <span className={r.status === "ARCHIVED" ? "text-zinc-500 line-through" : "text-white"}>{r.name}</span>
            <button className="ml-auto text-xs text-zinc-400 hover:text-white" data-testid={`role-rename-${r.name}`}
              onClick={() => { const n = window.prompt("Rename role", r.name); if (n && n.trim()) act(() => payApi.updateRole(r.id, { name: n }), "Role renamed"); }}>Rename</button>
            <button className="text-xs text-zinc-400 hover:text-white" data-testid={`role-archive-${r.name}`} onClick={() => act(() => payApi.updateRole(r.id, { archived: r.status === "ACTIVE" }), r.status === "ACTIVE" ? "Role archived" : "Role restored")}>
              <Archive className="inline h-3 w-3" aria-hidden /> {r.status === "ACTIVE" ? "Archive" : "Restore"}
            </button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="New role name" maxLength={60} data-testid="role-new-input" />
        <Button variant="secondary" disabled={!name.trim()} onClick={() => act(() => payApi.createRole(name), "Role added").then(() => setName(""))} data-testid="role-add-btn"><Plus className="h-4 w-4" aria-hidden /></Button>
      </div>
    </div>
  );
}

function AddRate({ roles, employees, onSaved }: { roles: JobRole[]; employees: { id: string; full_name: string }[]; onSaved: () => void }) {
  const [f, setF] = useState({ role_id: "", employee_id: "", dollars: "", eff: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));
  const cents = Math.round(Number(f.dollars) * 100);
  const save = async () => {
    setBusy(true);
    try {
      await payApi.createRate({ role_id: f.role_id, employee_id: f.employee_id || null, rate_cents: cents, effective_from: f.eff, reason: f.reason });
      toast.success("Rate added");
      setF({ ...f, dollars: "", eff: "", reason: "" });
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mb-6 grid gap-2 rounded-sm border border-white/10 p-4 sm:grid-cols-6" data-testid="rate-add-form">
      <select className={sel} value={f.role_id} onChange={(e) => set("role_id", e.target.value)} data-testid="rate-role-select"><option value="">Role…</option>{roles.filter((r) => r.status === "ACTIVE").map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
      <select className={sel} value={f.employee_id} onChange={(e) => set("employee_id", e.target.value)} data-testid="rate-employee-select"><option value="">Role default</option>{employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}</select>
      <TextInput placeholder="$/hour" inputMode="decimal" value={f.dollars} onChange={(e) => set("dollars", e.target.value)} data-testid="rate-amount-input" />
      <TextInput type="date" value={f.eff} onChange={(e) => set("eff", e.target.value)} data-testid="rate-effective-input" />
      <TextInput placeholder="Reason (required)" value={f.reason} onChange={(e) => set("reason", e.target.value)} data-testid="rate-reason-input" />
      <Button onClick={save} loading={busy} disabled={!f.role_id || !(cents > 0) || !f.eff || !f.reason.trim()} data-testid="rate-add-btn">Add rate</Button>
    </div>
  );
}

export function RatesTab() {
  const [nonce, setNonce] = useState(0);
  const loader = useCallback(() => Promise.all([payApi.roles(), payApi.rates(), adminApi.employees()]), [nonce]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad(loader);
  const refresh = () => setNonce((n) => n + 1);
  if (!data) return <LoadState error={error} loading={!error} testId="rates-load" />;
  const [roles, rates, employees] = data;
  const roleName = (id: string) => roles.find((r) => r.id === id)?.name ?? "?";
  const empName = (id: string | null) => (id ? employees.find((e) => e.id === id)?.full_name ?? "Employee" : "Role default");
  const correct = async (r: PayRate) => {
    const v = window.prompt(`Correct ${money(r.rate_cents)}/h from ${r.effective_from}. New $/hour:`);
    if (!v) return;
    const reason = window.prompt("Reason (required)") ?? "";
    try {
      await payApi.correctRate(r.id, Math.round(Number(v) * 100), reason);
      toast.success("Rate corrected; the original is kept as superseded");
      refresh();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const del = async (r: PayRate) => {
    try {
      await payApi.deleteRate(r.id);
      toast.success("Future rate deleted");
      refresh();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <section data-testid="pay-rates-tab" className="grid gap-8 lg:grid-cols-[260px_1fr]">
      <RoleList roles={roles} onChanged={refresh} />
      <div>
        <p className="mb-3 text-xs text-zinc-400">Rates are never overwritten: a new row supersedes from 00:00 Los Angeles time on its effective date. Rates already in effect or used by a calculation are locked. Resolution: employee override → role default → RATE_MISSING.</p>
        <AddRate roles={roles} employees={employees} onSaved={refresh} />
        <DataTable headers={["Role", "Applies to", "Rate", "Effective", "", ""]} testId="rates-table" empty={!rates.length}>
          {rates.map((r) => (
            <tr key={r.id} className="border-t border-white/5" data-testid={`rate-row-${r.id}`}>
              <td className="px-5 py-3 text-white">{roleName(r.role_id)}</td>
              <td className="px-5 py-3 text-zinc-300">{empName(r.employee_id)}{r.is_demo && <span className="ml-2 text-[10px] text-amber-300">DEMO</span>}</td>
              <td className="px-5 py-3 font-mono text-white" data-testid={`rate-amount-${r.id}`}>{money(r.rate_cents)}/h</td>
              <td className="px-5 py-3 font-mono text-xs text-zinc-400">{r.effective_from} → {r.effective_to ?? "open"}</td>
              <td className="px-5 py-3 text-xs text-zinc-500">{r.reason}{r.status === "SUPERSEDED_BY_CORRECTION" && <span className="ml-2 font-bold text-amber-300" data-testid={`rate-superseded-${r.id}`}>SUPERSEDED BY CORRECTION</span>}{r.correctable && <button onClick={() => correct(r)} className="ml-2 text-signal" data-testid={`rate-correct-${r.id}`}>Correct rate</button>}</td>
              <td className="px-5 py-3 text-right">
                {r.immutable ? <Lock className="inline h-4 w-4 text-zinc-500" aria-label="Locked" data-testid={`rate-locked-${r.id}`} />
                  : <button onClick={() => del(r)} className="text-zinc-400 hover:text-red-400" data-testid={`rate-delete-${r.id}`} aria-label="Delete future rate"><Trash2 className="h-4 w-4" /></button>}
              </td>
            </tr>
          ))}
        </DataTable>
      </div>
    </section>
  );
}
